import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { applyDocumentDirection, applyDocumentTitle, isRtl, SUPPORTED_LANGUAGES } from '@barahpay/shared';
import type { SupportedLanguage } from '@barahpay/shared';
import { i18next } from '@barahpay/shared';
import { setApiLanguage } from '../api/client';

const STORAGE_KEY = 'barahpay.admin.language';

interface LanguageState {
  language: SupportedLanguage;
  rtl: boolean;
  setLanguage: (language: SupportedLanguage) => void;
  toggleLanguage: () => void;
}

const LanguageContext = createContext<LanguageState | null>(null);

function initialLanguage(): SupportedLanguage {
  const stored = localStorage.getItem(STORAGE_KEY);

  if (stored && SUPPORTED_LANGUAGES.includes(stored as SupportedLanguage)) {
    return stored as SupportedLanguage;
  }

  return navigator.language.startsWith('ar') ? 'ar' : 'en';
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<SupportedLanguage>(() => {
    const initial = initialLanguage();
    void i18next.changeLanguage(initial);
    applyDocumentDirection(initial);
    applyDocumentTitle(i18next.t('common.portalTitle'));
    setApiLanguage(initial);
    return initial;
  });

  const setLanguage = useCallback((next: SupportedLanguage) => {
    localStorage.setItem(STORAGE_KEY, next);
    void i18next.changeLanguage(next);
    applyDocumentDirection(next);
    applyDocumentTitle(i18next.t('common.portalTitle'));
    setApiLanguage(next);
    setLanguageState(next);
  }, []);

  const value = useMemo<LanguageState>(
    () => ({
      language,
      rtl: isRtl(language),
      setLanguage,
      toggleLanguage: () => setLanguage(language === 'ar' ? 'en' : 'ar'),
    }),
    [language, setLanguage],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage(): LanguageState {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error('useLanguage must be used inside a LanguageProvider.');
  }

  return context;
}
